import { useEffect, useRef } from "react";

/**
 * useReveal — attaches an IntersectionObserver to reveal any descendant
 * element with `.reveal` (and the root if it carries `.reveal`).
 *
 * IMPORTANT — the "revealed" marker is a **data attribute**
 * (`data-revealed="true"`), not a class. React only rewrites the
 * `className` property when JSX changes; it never touches attributes
 * it doesn't own. This is critical for elements that combine `.reveal`
 * with stateful `className` template strings (accordions, toggles,
 * hover-state cards) — without this, a state change wipes the reveal
 * class and the element disappears.
 */
export default function useReveal() {
    const ref = useRef(null);

    useEffect(() => {
        const root = ref.current;
        if (!root) return;

        const targets = new Set();
        if (root.classList && root.classList.contains("reveal")) targets.add(root);
        root.querySelectorAll(".reveal").forEach((el) => targets.add(el));

        if (targets.size === 0) return;

        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        entry.target.setAttribute("data-revealed", "true");
                        observer.unobserve(entry.target);
                    }
                });
            },
            { threshold: 0.12, rootMargin: "0px 0px -40px 0px" }
        );

        targets.forEach((t) => observer.observe(t));
        return () => observer.disconnect();
    }, []);

    return ref;
}
